import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Typography,
  Icon,
  Tabs,
  Tab,
  CircularProgress,
  Rating,
  Chip
} from 'investira.react.components';
import { validators } from 'investira.sdk';
import hydrateAvaliacaoDiscriminacoes from '../_shared/hydrateAvaliacaoDiscriminacoes';

const AvaliacoesEditSugestoes = memo(props => {
  const {
    activeQuestionId,
    activeAliasQuestionId,
    activeQuestion = null,
    activeResposta = null,
    uploads = []
  } = props;

  const [tab, setTab] = useState(0);
  const [isFetching, setFecthing] = useState(true);

  const chipColor = (pPontos = 0) => {
    return pPontos < 1.8 ? 'error' : pPontos < 2.2 ? 'warning' : pPontos < 3 ? 'info' : 'primary';
  };

  const handleChangeTab = (pEvent, pNewValue) => {
    setTab(pNewValue);
  };

  useEffect(() => {
    setFecthing(true);
    setTimeout(() => {
      setFecthing(false);
    }, 500);
  }, [activeQuestionId]);

  const xDiscriminacoes = useMemo(() => {
    return hydrateAvaliacaoDiscriminacoes({
      resposta_tipo: activeQuestion?.resposta_tipo,
      resposta_dominio: activeQuestion?.resposta_dominio,
      resposta_valor: activeResposta?.valor
    });
  }, [activeQuestion?.resposta_dominio, activeQuestion?.resposta_tipo, activeResposta?.valor]);
  const xComentarioGestor =
    activeResposta?.comentario || 'Nenhum comentário informado pelo gestor.';
  const xAnexosQuestao = useMemo(() => {
    const xQuestionId = Number(activeQuestionId);

    return (Array.isArray(uploads) ? uploads : []).filter(pUpload => {
      return Number(pUpload?.avaliacao_questao_id) === xQuestionId;
    });
  }, [activeQuestionId, uploads]);
  const xAnexosAvaliacao = useMemo(() => {
    return (Array.isArray(uploads) ? uploads : []).filter(pUpload => {
      return validators.isNull(pUpload?.avaliacao_questao_id);
    });
  }, [uploads]);

  const pvRenderAnexos = (pTitulo, pAnexos = []) => {
    return (
      <Stack gap={1}>
        <Typography color="textPrimary" variant="body2" sx={{ fontWeight: 600 }}>
          {pTitulo}
        </Typography>
        {pAnexos.length > 0 ? (
          pAnexos.map((pAnexo, pIndex) => (
            <Stack
              key={`${pTitulo}-${pAnexo?.path || pAnexo?.arquivo || pIndex}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                backgroundColor: 'background.paper'
              }}>
              <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                {/* <Icon iconName="paper_clip" size={16} color="textSecondary" /> */}
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {pAnexo?.arquivo || 'Arquivo'}
                </Typography>
              </Stack>
              {pAnexo?.path && (
                <a
                  href={pAnexo.path}
                  download={pAnexo?.arquivo || true}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Baixar ${pAnexo?.arquivo || 'arquivo'}`}
                  style={{ lineHeight: 0, color: 'inherit' }}>
                  <Icon iconName="download" size={18} color="primary" />
                </a>
              )}
            </Stack>
          ))
        ) : (
          <Typography color="textSecondary" variant="caption">
            Nenhum anexo disponível.
          </Typography>
        )}
      </Stack>
    );
  };

  const headerRender = pActiveAliasQuestionId => {
    return (
      <Stack>
        <Typography color="textSecondary" variant="caption">
          Apoio contextual
        </Typography>
        <Typography color="textPrimary" variant="h5">
          Questão {pActiveAliasQuestionId}
        </Typography>
      </Stack>
    );
  };

  if (isFetching) {
    return (
      <Stack gap={3}>
        {headerRender(activeAliasQuestionId)}
        <Stack gap={2} sx={{ minHeight: '200px', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={2} mb={2}>
      {headerRender(activeAliasQuestionId)}
      <Stack gap={2} mb={1}>
        <Stack gap={2}>
          <Typography
            color="textSecondary"
            variant="caption"
            sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
            Discriminação
          </Typography>
          <Stack gap={1.5}>
            {xDiscriminacoes.length === 0 && (
              <Typography color="textSecondary" variant="caption">
                Nenhuma discriminação disponível para esta questão.
              </Typography>
            )}
            {xDiscriminacoes.map((xDiscriminacao, xIndex) => {
              const xIsRespostaGestor = xDiscriminacao.resposta_gestor === true;

              return (
                <Stack key={xDiscriminacao.id}>
                  <Stack
                    sx={{
                      border: '1px solid',
                      borderColor: xIsRespostaGestor ? 'warning.main' : 'secondary.main',
                      p: 2,
                      borderRadius: 2,
                      position: 'relative'
                    }}>
                    {xIsRespostaGestor && (
                      <Stack
                        direction="row"
                        gap={1}
                        sx={{
                          backgroundColor: 'secondary.darkness',
                          px: 1.5,
                          position: 'absolute',
                          top: '-10px',
                          right: '8px'
                        }}>
                        <Icon iconName="user_tie" color="warn" size={16} />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                          Resposta do gestor
                        </Typography>
                      </Stack>
                    )}
                    <Stack gap={1}>
                      <Typography
                        color="textPrimary"
                        variant="caption"
                        sx={{ fontWeight: xIsRespostaGestor ? 500 : 400 }}>
                        {xDiscriminacao.discriminacao}
                      </Typography>
                      <Stack
                        direction="row"
                        gap={2}
                        alignItems="center"
                        justifyContent="space-between">
                        <Rating
                          name={`read-only-${xDiscriminacao.id}`}
                          value={xIndex || 0}
                          max={xDiscriminacoes.length - 1}
                          readOnly
                          size="small"
                        />
                        {!validators.isNull(xDiscriminacao.pontos) && (
                          <Chip
                            size="small"
                            label={`${xDiscriminacao.pontos > 0 ? '+' : ''}${
                              xDiscriminacao.pontos
                            } ponto${Math.abs(xDiscriminacao.pontos) === 1 ? '' : 's'}`}
                            color={chipColor(xDiscriminacao.pontos)}
                            sx={{ width: 'fit-content' }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      {/* <Divider /> */}
      <Stack gap={1}>
        <Typography
          color="textSecondary"
          variant="caption"
          sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
          Evidências complementares
        </Typography>
        <Tabs value={tab} onChange={handleChangeTab}>
          <Tab label="GESTOR" />
          <Tab label="ANEXOS" />
        </Tabs>
        <Stack>
          {tab === 0 && (
            <>
              <Stack my={1} gap={2}>
                <Stack direction="row" alignItens="center" justifyContent="space-between">
                  <Stack direction="row" alignItens="center" gap={1}>
                    <Icon iconName="user_tie" color="primary" size={18} />
                    <Typography color="textPrimary" variant="body2" sx={{ fontWeight: 600 }}>
                      Comentário do gestor
                    </Typography>
                  </Stack>
                </Stack>
                <Stack gap={1}>
                  <Typography color="textPrimary" variant="caption">
                    {xComentarioGestor}
                  </Typography>
                </Stack>
              </Stack>
            </>
          )}

          {tab === 1 && (
            <>
              <Stack my={1} gap={2}>
                {pvRenderAnexos('Anexos da questão', xAnexosQuestao)}
                {pvRenderAnexos('Anexos da avaliação', xAnexosAvaliacao)}
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

export default AvaliacoesEditSugestoes;
